import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { addHours, startOfDay } from "date-fns/esm";
import type Calendar from "nylas/lib/models/calendar";
import { useIntl } from "react-intl";

import { Deal } from "@mapmycustomers/shared/types/entity";
import { Button, LoadingSpinner, Modal } from "@mapmycustomers/ui";

import { goToCalendarSettings } from "@app/component/preview/DealRecordPane/store/actions";
import { hideEntityView } from "@app/store/entityView/actions";
import {
  areCalendarsLoading,
  getCalendars,
  getNylasCalendarInfo,
  isCreatingCalendarEventForDealClosingDate,
} from "@app/store/nylas";
import { createCalendarEventForDealClosingDate, fetchCalendars } from "@app/store/nylas/actions";
import { RootState } from "@app/store/rootReducer";
import NylasInfo from "@app/types/nylas/NylasInfo";
import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";

import styles from "./AddEventToNylasModal.module.scss";
import NoSyncModal from "./NoSyncModal";
import TargetCalendarAndInfo from "./TargetCalendarAndInfo";

interface Props {
  calendars: Calendar[];
  calendarsLoading: boolean;
  createCalendarEvent: (payload: { callback: () => void; deal: Deal; eventDateTime: Date }) => void;
  createCalendarEventForDealClosingDateLoading: boolean;
  deal: Deal;
  fetchCalendars: () => void;
  goToCalendarSettings: typeof goToCalendarSettings;
  hideEntityView: typeof hideEntityView;
  nylasInfo: NylasInfo | undefined;
  onHide: () => void;
}

const AddEventToNylasModal: React.FC<Props> = ({
  calendars,
  calendarsLoading,
  createCalendarEvent,
  createCalendarEventForDealClosingDateLoading,
  deal,
  fetchCalendars,
  goToCalendarSettings,
  hideEntityView,
  nylasInfo,
  onHide,
}) => {
  const intl = useIntl();

  useEffect(() => {
    if (nylasInfo?.calendarId != null && !calendars.length) {
      fetchCalendars();
    }
  }, [calendars, nylasInfo, fetchCalendars]);

  const calendar = calendars.find(({ id }) => id === nylasInfo?.calendarId);

  const handleAddClick = useCallback(() => {
    if (!deal.closingDate) {
      return;
    }
    let eventDateTime = parseApiDate(deal.closingDate);
    if (!isValidDate(eventDateTime) || !calendar) {
      return;
    }
    eventDateTime = addHours(startOfDay(eventDateTime), 9); // 9am
    createCalendarEvent({ callback: onHide, deal, eventDateTime });
  }, [calendar, createCalendarEvent, deal, onHide]);

  const handleGoToCalendarSettings = useCallback(() => {
    goToCalendarSettings();
    hideEntityView();
  }, [goToCalendarSettings, hideEntityView]);

  return !nylasInfo?.calendarId ? (
    <NoSyncModal onGoToCalendarSettings={handleGoToCalendarSettings} onHide={onHide} />
  ) : (
    <Modal
      footer={
        <div className={styles.footer}>
          <Button onClick={handleGoToCalendarSettings} type="link">
            {intl.formatMessage({
              id: "dealRecord.addEventToNylasModal.manageButton",
              defaultMessage: "Manage Calendar Sync",
              description: "Manage button in the add-event-to-nylas modal in Deal preview pane",
            })}
          </Button>
          <Button
            disabled={createCalendarEventForDealClosingDateLoading}
            loading={createCalendarEventForDealClosingDateLoading}
            onClick={handleAddClick}
            type="primary"
          >
            {intl.formatMessage({
              id: "dealRecord.addEventToNylasModal.ok",
              defaultMessage: "Add to Calendar",
              description: "Ok button in the add-event-to-nylas modal in Deal preview pane",
            })}
          </Button>
        </div>
      }
      onCancel={onHide}
      title={intl.formatMessage({
        id: "dealRecord.addEventToNylasModal.title",
        defaultMessage: "Add close date to your calendar",
        description: "Title of a add-event-to-nylas modal in Deal preview pane",
      })}
      visible
    >
      {calendarsLoading || !calendar ? (
        <LoadingSpinner />
      ) : (
        <TargetCalendarAndInfo calendar={calendar} />
      )}
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  calendars: getCalendars(state),
  calendarsLoading: areCalendarsLoading(state),
  createCalendarEventForDealClosingDateLoading: isCreatingCalendarEventForDealClosingDate(state),
  nylasInfo: getNylasCalendarInfo(state),
});

const mapDispatchToProps = {
  createCalendarEvent: createCalendarEventForDealClosingDate.request,
  fetchCalendars: fetchCalendars.request,
  goToCalendarSettings,
  hideEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddEventToNylasModal);
